import { useEffect, useRef, useCallback } from 'react';
import { useMutation } from '@apollo/client/react';
import { REFRESH_AUTH_TOKEN } from '@/graphql/mutations';
import { getAccessToken, getRefreshToken, saveTokens, clearTokens, checkTokenExpiry } from '@/lib/token-utils';

/**
 * Hook to proactively refresh authentication tokens before they expire
 * This prevents "authentication required" errors by keeping tokens fresh
 */
export function useTokenRefresh() {
  const isRefreshingRef = useRef(false);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshAttemptRef = useRef<number>(0);
  
  const [refreshAuthToken] = useMutation<{
    refreshAuthToken: {
      success: boolean;
      tokens?: {
        accessToken: string;
        refreshToken: string;
        expiresIn?: number;
      };
      error?: string;
    };
  }>(REFRESH_AUTH_TOKEN);

  const performTokenRefresh = useCallback(async () => {
    // Prevent multiple simultaneous refresh attempts
    if (isRefreshingRef.current) {
      console.log('[Token Refresh Hook] Already refreshing, skipping...');
      return false;
    }

    // Prevent refresh attempts more frequent than once per minute
    const now = Date.now();
    const timeSinceLastAttempt = now - lastRefreshAttemptRef.current;
    if (timeSinceLastAttempt < 60000) { // 1 minute
      console.log('[Token Refresh Hook] Too soon since last attempt, skipping...');
      return false;
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      console.warn('[Token Refresh Hook] No refresh token available');
      return false;
    }

    isRefreshingRef.current = true;
    lastRefreshAttemptRef.current = now;

    try {
      console.log('[Token Refresh Hook] Proactively refreshing access token...');
      
      const result = await refreshAuthToken({
        variables: { refreshToken },
      });

      const refreshResult = result.data?.refreshAuthToken;
      
      if (refreshResult?.success && refreshResult?.tokens) {
        console.log('[Token Refresh Hook] Token refresh successful');
        saveTokens(
          refreshResult.tokens.accessToken,
          refreshResult.tokens.refreshToken
        );
        return true;
      } else {
        console.warn('[Token Refresh Hook] Token refresh failed:', refreshResult?.error);
        // If refresh fails, clear tokens and redirect to login
        clearTokens();
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          console.warn('[Token Refresh Hook] Redirecting to login...');
          window.location.href = '/login';
        }
        return false;
      }
    } catch (error) {
      console.error('[Token Refresh Hook] Error refreshing token:', error);
      // On error, clear tokens and redirect to login
      clearTokens();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        console.warn('[Token Refresh Hook] Redirecting to login due to error...');
        window.location.href = '/login';
      }
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [refreshAuthToken]);

  const scheduleNextRefresh = useCallback(() => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    
    // Only schedule refresh if we have both tokens
    if (!accessToken || !refreshToken) {
      console.log('[Token Refresh Hook] No tokens available, not scheduling refresh');
      return;
    }

    const { isExpired, isExpiringSoon, expiresAt } = checkTokenExpiry(accessToken, 5 * 60); // 5 minute buffer

    if (isExpired) {
      // Token is already expired, refresh immediately
      console.log('[Token Refresh Hook] Token expired, refreshing immediately');
      performTokenRefresh().then(() => {
        // Schedule next check after refresh
        scheduleNextRefresh();
      });
      return;
    }

    if (isExpiringSoon && expiresAt) {
      // Token will expire soon, refresh now
      const timeUntilExpiry = expiresAt - Date.now();
      console.log(`[Token Refresh Hook] Token expiring in ${Math.round(timeUntilExpiry / 1000 / 60)} minutes, refreshing now`);
      performTokenRefresh().then(() => {
        // Schedule next check after refresh
        scheduleNextRefresh();
      });
      return;
    }

    // Token is still valid, schedule refresh for when it's close to expiring
    if (expiresAt) {
      // Refresh 5 minutes before expiry
      const refreshBuffer = 5 * 60 * 1000; // 5 minutes in milliseconds
      const timeUntilRefresh = Math.max(0, expiresAt - Date.now() - refreshBuffer);
      
      console.log(`[Token Refresh Hook] Token valid, scheduling refresh in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`);
      
      refreshTimeoutRef.current = setTimeout(() => {
        performTokenRefresh().then(() => {
          // Schedule next check after refresh
          scheduleNextRefresh();
        });
      }, timeUntilRefresh);
    } else {
      // If we can't determine expiry, check again in 5 minutes
      console.log('[Token Refresh Hook] Could not determine token expiry, checking again in 5 minutes');
      refreshTimeoutRef.current = setTimeout(() => {
        scheduleNextRefresh();
      }, 5 * 60 * 1000);
    }
  }, [performTokenRefresh]);

  useEffect(() => {
    // Start the token refresh cycle
    scheduleNextRefresh();

    // Also check every minute as a backup (in case the main timer fails)
    const backupInterval = setInterval(() => {
      const accessToken = getAccessToken();
      if (accessToken) {
        const { isExpiringSoon } = checkTokenExpiry(accessToken, 5 * 60); // 5 minute buffer
        if (isExpiringSoon && !isRefreshingRef.current) {
          console.log('[Token Refresh Hook] Backup check detected expiring token');
          performTokenRefresh().then(() => {
            scheduleNextRefresh();
          });
        }
      }
    }, 60 * 1000); // Check every minute

    // Cleanup on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      clearInterval(backupInterval);
    };
  }, [scheduleNextRefresh, performTokenRefresh]);

  // Return a manual refresh function in case it's needed
  return {
    refreshNow: performTokenRefresh,
  };
}

